import { Group, IconButton, Text } from '@melio/penny';
import { AccountingPlatform } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';

import { useActivitiesNavigate } from '../../../../utils';
import { SchedulePaymentMainButton } from './SchedulePaymentMainButton';
import { SyncNowButton } from './SyncNowButton';

type Props = {
  accountingPlatform?: AccountingPlatform;
  analyticsProperties: Record<string, string>;
  shouldShowNewPaymentButton: boolean;
  onClickSchedulePayment: VoidFunction;
  goToSettingsAccountSoftware: VoidFunction;
};

export const DashboardHeader = ({
  accountingPlatform,
  analyticsProperties,
  shouldShowNewPaymentButton,
  onClickSchedulePayment,
  goToSettingsAccountSoftware,
}: Props) => {
  const { formatMessage } = useMelioIntl();
  const { settings } = useConfig();
  const { navigateToSettingsPage, navigateToSettingsSupportPage } = useActivitiesNavigate();

  const renderSyncNowButton = () => (
    <SyncNowButton
      accountingPlatform={accountingPlatform}
      analyticsProperties={analyticsProperties}
      goToSettingsAccountSoftware={goToSettingsAccountSoftware}
    />
  );

  const renderPaymentButton = (shouldShowNewPaymentButton: boolean) => {
    if (shouldShowNewPaymentButton) {
      return <SchedulePaymentMainButton onClick={onClickSchedulePayment} />;
    }

    return null;
  };

  const renderSettingsButton = () => {
    if (settings.isEmbeddedExperience) {
      return (
        <IconButton
          aria-label={formatMessage('activities.payDashboard.header.nav.settings.ariaLabel')}
          variant="naked"
          size="large"
          icon="settings"
          onClick={navigateToSettingsPage}
        />
      );
    }
    return null;
  };

  const renderSupportButton = () => {
    if (settings.isEmbeddedExperience) {
      return (
        <IconButton
          aria-label={formatMessage('activities.payDashboard.header.nav.support.ariaLabel')}
          variant="naked"
          size="large"
          icon="help-circle"
          onClick={navigateToSettingsSupportPage}
        />
      );
    }
    return null;
  };

  return (
    <Group justifyContent="space-between" spacing="m">
      <Text
        as="h2"
        textStyle="heading1Semi"
        aria-live="polite"
        aria-label={formatMessage('activities.payDashboard.title.ariaLabel')}
      >
        {formatMessage('activities.payDashboard.title')}
      </Text>
      <Group variant="horizontal" spacing="m" alignItems="center">
        {renderSyncNowButton()}
        {renderSupportButton()}
        {renderSettingsButton()}
        {renderPaymentButton(shouldShowNewPaymentButton)}
      </Group>
    </Group>
  );
};
